import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  LinearProgress,
  Tabs,
  Tab,
  Stack,
  Alert,
} from '@mui/material';
import { Visibility, Computer, Smartphone, Tablet, LocationOn } from '@mui/icons-material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import config from '../config';
import { authenticatedFetch } from '../utils/tokenUtils';
import Loading from '../components/Loading';

// Country code to full name mapping (ISO 3166-1 alpha-2)
const countryNames = {
  AF: 'Afghanistan',
  AL: 'Albania',
  DZ: 'Algeria',
  AS: 'American Samoa',
  AD: 'Andorra',
  AO: 'Angola',
  AI: 'Anguilla',
  AQ: 'Antarctica',
  AG: 'Antigua and Barbuda',
  AR: 'Argentina',
  AM: 'Armenia',
  AW: 'Aruba',
  AU: 'Australia',
  AT: 'Austria',
  AZ: 'Azerbaijan',
  BS: 'Bahamas',
  BH: 'Bahrain',
  BD: 'Bangladesh',
  BB: 'Barbados',
  BY: 'Belarus',
  BE: 'Belgium',
  BZ: 'Belize',
  BJ: 'Benin',
  BM: 'Bermuda',
  BT: 'Bhutan',
  BO: 'Bolivia',
  BA: 'Bosnia and Herzegovina',
  BW: 'Botswana',
  BR: 'Brazil',
  IO: 'British Indian Ocean Territory',
  VG: 'British Virgin Islands',
  BN: 'Brunei',
  BG: 'Bulgaria',
  BF: 'Burkina Faso',
  BI: 'Burundi',
  KH: 'Cambodia',
  CM: 'Cameroon',
  CA: 'Canada',
  CV: 'Cape Verde',
  KY: 'Cayman Islands',
  CF: 'Central African Republic',
  TD: 'Chad',
  CL: 'Chile',
  CN: 'China',
  CX: 'Christmas Island',
  CC: 'Cocos Islands',
  CO: 'Colombia',
  KM: 'Comoros',
  CK: 'Cook Islands',
  CR: 'Costa Rica',
  HR: 'Croatia',
  CU: 'Cuba',
  CW: 'Curaçao',
  CY: 'Cyprus',
  CZ: 'Czechia',
  CD: 'Democratic Republic of the Congo',
  DK: 'Denmark',
  DJ: 'Djibouti',
  DM: 'Dominica',
  DO: 'Dominican Republic',
  TL: 'East Timor',
  EC: 'Ecuador',
  EG: 'Egypt',
  SV: 'El Salvador',
  GQ: 'Equatorial Guinea',
  ER: 'Eritrea',
  EE: 'Estonia',
  SZ: 'Eswatini',
  ET: 'Ethiopia',
  FK: 'Falkland Islands',
  FO: 'Faroe Islands',
  FJ: 'Fiji',
  FI: 'Finland',
  FR: 'France',
  GF: 'French Guiana',
  PF: 'French Polynesia',
  TF: 'French Southern Territories',
  GA: 'Gabon',
  GM: 'Gambia',
  GE: 'Georgia',
  DE: 'Germany',
  GH: 'Ghana',
  GI: 'Gibraltar',
  GR: 'Greece',
  GL: 'Greenland',
  GD: 'Grenada',
  GP: 'Guadeloupe',
  GU: 'Guam',
  GT: 'Guatemala',
  GG: 'Guernsey',
  GN: 'Guinea',
  GW: 'Guinea-Bissau',
  GY: 'Guyana',
  HT: 'Haiti',
  HN: 'Honduras',
  HK: 'Hong Kong',
  HU: 'Hungary',
  IS: 'Iceland',
  IN: 'India',
  ID: 'Indonesia',
  IR: 'Iran',
  IQ: 'Iraq',
  IE: 'Ireland',
  IM: 'Isle of Man',
  IL: 'Israel',
  IT: 'Italy',
  JM: 'Jamaica',
  JP: 'Japan',
  JE: 'Jersey',
  JO: 'Jordan',
  KZ: 'Kazakhstan',
  KE: 'Kenya',
  KI: 'Kiribati',
  XK: 'Kosovo',
  KW: 'Kuwait',
  KG: 'Kyrgyzstan',
  LA: 'Laos',
  LV: 'Latvia',
  LB: 'Lebanon',
  LS: 'Lesotho',
  LR: 'Liberia',
  LY: 'Libya',
  LI: 'Liechtenstein',
  LT: 'Lithuania',
  LU: 'Luxembourg',
  MO: 'Macao',
  MG: 'Madagascar',
  MW: 'Malawi',
  MY: 'Malaysia',
  MV: 'Maldives',
  ML: 'Mali',
  MT: 'Malta',
  MH: 'Marshall Islands',
  MQ: 'Martinique',
  MR: 'Mauritania',
  MU: 'Mauritius',
  YT: 'Mayotte',
  MX: 'Mexico',
  FM: 'Micronesia',
  MD: 'Moldova',
  MC: 'Monaco',
  MN: 'Mongolia',
  ME: 'Montenegro',
  MS: 'Montserrat',
  MA: 'Morocco',
  MZ: 'Mozambique',
  MM: 'Myanmar',
  NA: 'Namibia',
  NR: 'Nauru',
  NP: 'Nepal',
  NL: 'Netherlands',
  NC: 'New Caledonia',
  NZ: 'New Zealand',
  NI: 'Nicaragua',
  NE: 'Niger',
  NG: 'Nigeria',
  NU: 'Niue',
  NF: 'Norfolk Island',
  KP: 'North Korea',
  MK: 'North Macedonia',
  MP: 'Northern Mariana Islands',
  NO: 'Norway',
  OM: 'Oman',
  PK: 'Pakistan',
  PW: 'Palau',
  PS: 'Palestine',
  PA: 'Panama',
  PG: 'Papua New Guinea',
  PY: 'Paraguay',
  PE: 'Peru',
  PH: 'Philippines',
  PN: 'Pitcairn Islands',
  PL: 'Poland',
  PT: 'Portugal',
  PR: 'Puerto Rico',
  QA: 'Qatar',
  CG: 'Republic of the Congo',
  RE: 'Réunion',
  RO: 'Romania',
  RU: 'Russia',
  RW: 'Rwanda',
  BL: 'Saint Barthélemy',
  SH: 'Saint Helena',
  KN: 'Saint Kitts and Nevis',
  LC: 'Saint Lucia',
  MF: 'Saint Martin',
  PM: 'Saint Pierre and Miquelon',
  VC: 'Saint Vincent and the Grenadines',
  WS: 'Samoa',
  SM: 'San Marino',
  ST: 'São Tomé and Príncipe',
  SA: 'Saudi Arabia',
  SN: 'Senegal',
  RS: 'Serbia',
  SC: 'Seychelles',
  SL: 'Sierra Leone',
  SG: 'Singapore',
  SX: 'Sint Maarten',
  SK: 'Slovakia',
  SI: 'Slovenia',
  SB: 'Solomon Islands',
  SO: 'Somalia',
  ZA: 'South Africa',
  GS: 'South Georgia',
  KR: 'South Korea',
  SS: 'South Sudan',
  ES: 'Spain',
  LK: 'Sri Lanka',
  SD: 'Sudan',
  SR: 'Suriname',
  SJ: 'Svalbard and Jan Mayen',
  SE: 'Sweden',
  CH: 'Switzerland',
  SY: 'Syria',
  TW: 'Taiwan',
  TJ: 'Tajikistan',
  TZ: 'Tanzania',
  TH: 'Thailand',
  TG: 'Togo',
  TK: 'Tokelau',
  TO: 'Tonga',
  TT: 'Trinidad and Tobago',
  TN: 'Tunisia',
  TR: 'Turkey',
  TM: 'Turkmenistan',
  TC: 'Turks and Caicos Islands',
  TV: 'Tuvalu',
  UG: 'Uganda',
  UA: 'Ukraine',
  AE: 'United Arab Emirates',
  GB: 'United Kingdom',
  US: 'United States',
  UY: 'Uruguay',
  UZ: 'Uzbekistan',
  VU: 'Vanuatu',
  VA: 'Vatican City',
  VE: 'Venezuela',
  VN: 'Vietnam',
  VI: 'Virgin Islands',
  WF: 'Wallis and Futuna',
  EH: 'Western Sahara',
  YE: 'Yemen',
  ZM: 'Zambia',
  ZW: 'Zimbabwe',
  UN: 'Unknown',
  XX: 'Unknown',
  ZZ: 'Unknown',
  null: 'Unknown',
  undefined: 'Unknown',
  '': 'Unknown',
};
const getCountryName = code => countryNames[code] || code || 'Unknown';

const AdminAnalytics = () => {
  const [loading, setLoading] = useState(true);
  const [selectedTab, setSelectedTab] = useState(0);
  const [period, setPeriod] = useState('30d');
  const [analyticsData, setAnalyticsData] = useState(null);
  const [detailedData, setDetailedData] = useState({ data: [], pagination: {} });
  const [_currentPage, setCurrentPage] = useState(1);
  const [error, setError] = useState(null);

  const fetchAnalyticsSummary = useCallback(async () => {
    try {
      setLoading(true);
      const response = await authenticatedFetch(
        `${config.apiBaseUrl}/analytics/summary?period=${period}`
      );
      if (response.ok) {
        const data = await response.json();
        setAnalyticsData(data);
      } else {
        setError('Failed to fetch analytics data');
      }
    } catch (error) {
      setError('Error fetching analytics data');
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  }, [period]);

  const fetchDetailedData = useCallback(
    async (page = 1) => {
      try {
        const response = await authenticatedFetch(
          `${config.apiBaseUrl}/analytics/data?page=${page}&limit=20&period=${period}`
        );
        if (response.ok) {
          const data = await response.json();
          setDetailedData(data);
        }
      } catch (error) {
        console.error('Error fetching detailed data:', error);
      }
    },
    [period]
  );

  useEffect(() => {
    fetchAnalyticsSummary();
    fetchDetailedData();

    // Set up real-time updates
    // const interval = setInterval(() => {
    //   fetchRealTimeData();
    // }, 30000); // Update every 30 seconds

    return () => {
      // clearInterval(interval);
    };
  }, [fetchAnalyticsSummary, fetchDetailedData]);

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  const handlePeriodChange = event => {
    setPeriod(event.target.value);
    setCurrentPage(1);
  };

  const formatNumber = num => {
    return new Intl.NumberFormat().format(num);
  };

  const formatDate = dateString => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getDeviceIcon = deviceType => {
    switch (deviceType) {
      case 'mobile':
        return <Smartphone fontSize="small" />;
      case 'tablet':
        return <Tablet fontSize="small" />;
      case 'desktop':
        return <Computer fontSize="small" />;
      default:
        return <Computer fontSize="small" />;
    }
  };

  const renderSummaryCards = () => {
    if (!analyticsData) return null;

    const { summary } = analyticsData;

    const cards = [
      {
        title: 'Total Visits',
        value: formatNumber(summary.totalVisits),
        icon: <Visibility />,
        color: '#DAA520',
      },
      // Removed Unique Visitors card
    ];

    return (
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {cards.map((card, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Card
              sx={{
                height: '100%',
                background: `linear-gradient(135deg, ${card.color}15, ${card.color}05)`,
                border: `1px solid ${card.color}30`,
              }}
            >
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Box
                    sx={{
                      color: card.color,
                      mr: 1,
                    }}
                  >
                    {card.icon}
                  </Box>
                  <Typography
                    variant="h6"
                    component="div"
                    sx={{ color: card.color, fontWeight: 'bold' }}
                  >
                    {card.value}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {card.title}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    );
  };

  const renderDailyVisitsChart = () => {
    if (!analyticsData?.dailyVisits || analyticsData.dailyVisits.length === 0) return null;
    // Format dates for chart labels
    const data = analyticsData.dailyVisits.map(d => ({
      ...d,
      date: new Date(d.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    }));
    return (
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ color: '#000000', fontWeight: 'bold' }}>
            Daily Visits
          </Typography>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={data} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis allowDecimals={false} />
              <Tooltip />
              <Line
                type="monotone"
                dataKey="count"
                stroke="#DAA520"
                strokeWidth={2}
                dot={{ r: 3 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
    );
  };

  const renderDeviceStats = () => {
    if (!analyticsData?.deviceStats) return null;

    const total = analyticsData.deviceStats.reduce((sum, stat) => sum + stat.count, 0);

    return (
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ color: '#000000', fontWeight: 'bold' }}>
            Device Distribution
          </Typography>
          <Stack spacing={2}>
            {analyticsData.deviceStats.map((stat, index) => {
              const percentage = total > 0 ? (stat.count / total) * 100 : 0;
              return (
                <Box key={index}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      mb: 1,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      {getDeviceIcon(stat.deviceType)}
                      <Typography variant="body2" sx={{ ml: 1, textTransform: 'capitalize' }}>
                        {stat.deviceType}
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                      {formatNumber(stat.count)} ({percentage.toFixed(1)}%)
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={percentage}
                    sx={{
                      height: 8,
                      borderRadius: 4,
                      bgcolor: 'rgba(218, 165, 32, 0.1)',
                      '& .MuiLinearProgress-bar': {
                        bgcolor: '#DAA520',
                      },
                    }}
                  />
                </Box>
              );
            })}
          </Stack>
        </CardContent>
      </Card>
    );
  };

  const renderTopCountries = () => {
    if (!analyticsData?.topCountries) return null;

    return (
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ color: '#000000', fontWeight: 'bold' }}>
            Top Countries
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Rank</TableCell>
                  <TableCell>Country</TableCell>
                  <TableCell align="right">Visits</TableCell>
                  <TableCell align="right">Percentage</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {analyticsData.topCountries.map((country, index) => {
                  const percentage = (country.count / analyticsData.summary.totalVisits) * 100;
                  return (
                    <TableRow key={index}>
                      <TableCell>{index + 1}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <LocationOn sx={{ fontSize: '1rem', mr: 1, color: '#DAA520' }} />
                          {getCountryName(country.country)}
                        </Box>
                      </TableCell>
                      <TableCell align="right">{formatNumber(country.count)}</TableCell>
                      <TableCell align="right">{percentage.toFixed(1)}%</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    );
  };

  const renderTopBrowsers = () => {
    if (!analyticsData?.topBrowsers) return null;

    return (
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ color: '#000000', fontWeight: 'bold' }}>
            Top Browsers
          </Typography>
          <Stack spacing={2}>
            {analyticsData.topBrowsers.map((browser, index) => {
              const percentage = (browser.count / analyticsData.summary.totalVisits) * 100;
              return (
                <Box key={index}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      mb: 1,
                    }}
                  >
                    <Typography variant="body2">{browser.browser}</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                      {formatNumber(browser.count)} ({percentage.toFixed(1)}%)
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={percentage}
                    sx={{
                      height: 6,
                      borderRadius: 3,
                      bgcolor: 'rgba(218, 165, 32, 0.1)',
                      '& .MuiLinearProgress-bar': {
                        bgcolor: '#DAA520',
                      },
                    }}
                  />
                </Box>
              );
            })}
          </Stack>
        </CardContent>
      </Card>
    );
  };

  const renderDetailedData = () => {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ color: '#000000', fontWeight: 'bold' }}>
            Detailed Visit Data
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Device</TableCell>
                  <TableCell>Browser</TableCell>
                  <TableCell>Country</TableCell>
                  <TableCell>Page</TableCell>
                  <TableCell>Returning</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {detailedData.data.map(visit => (
                  <TableRow key={visit.id}>
                    <TableCell>{formatDate(visit.createdAt)}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {getDeviceIcon(visit.deviceType)}
                        <Typography variant="body2" sx={{ ml: 0.5, textTransform: 'capitalize' }}>
                          {visit.deviceType}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>{visit.browser}</TableCell>
                    <TableCell>{getCountryName(visit.country)}</TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}
                      >
                        {visit.pageTitle || visit.pageUrl}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={visit.isReturning ? 'Returning' : 'New'}
                        color={visit.isReturning ? 'success' : 'primary'}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Loading />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom sx={{ color: '#000000', fontWeight: 'bold' }}>
          Analytics
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Track and analyze your mirmire's visitor data, including location, device types, and user
          behavior.
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <FormControl sx={{ minWidth: 120 }}>
          <InputLabel>Period</InputLabel>
          <Select value={period} label="Period" onChange={handlePeriodChange}>
            <MenuItem value="7d">Last 7 days</MenuItem>
            <MenuItem value="30d">Last 30 days</MenuItem>
            <MenuItem value="90d">Last 90 days</MenuItem>
            <MenuItem value="1y">Last year</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <Tabs value={selectedTab} onChange={handleTabChange} sx={{ mb: 3 }}>
        <Tab label="Overview" />
        <Tab label="Detailed Data" />
      </Tabs>

      {selectedTab === 0 && (
        <>
          {renderSummaryCards()}
          {renderDailyVisitsChart()}
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              {renderDeviceStats()}
            </Grid>
            <Grid item xs={12} md={6}>
              {renderTopBrowsers()}
            </Grid>
            <Grid item xs={12}>
              {renderTopCountries()}
            </Grid>
          </Grid>
        </>
      )}

      {selectedTab === 1 && renderDetailedData()}
    </Container>
  );
};

export default AdminAnalytics;
